import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Typography,
  Tag,
  Space,
  Spin,
  Grid,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import regionService from '../../services/regionService';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const RegionManager = () => {
  const screens = useBreakpoint();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [form] = Form.useForm();

  const fetchRegions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await regionService.getAll();
      setRegions(response.regions || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch regions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  const handleOpenDialog = (region = null) => {
    if (region) {
      setEditingRegion(region);
      form.setFieldsValue({
        name: region.name || '',
        description: region.description || '',
      });
    } else {
      setEditingRegion(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRegion(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (editingRegion) {
        await regionService.update(editingRegion.id, values);
      } else {
        await regionService.create(values);
      }

      handleCloseDialog();
      fetchRegions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save region');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Haqiqatan ham ushbu regionni o\'chirmoqchimisiz?')) {
      try {
        await regionService.delete(id);
        fetchRegions();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete region');
      }
    }
  };

  if (loading && !regions.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Nomi',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Tavsif',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || 'Mavjud emas',
      responsive: ['md'],
    },
    {
      title: 'Sotuvchilar soni',
      key: 'sellers',
      render: (_, record) => record.Sellers ? record.Sellers.length : 0,
      responsive: ['sm'],
    },
    {
      title: 'Holati',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.is_active ? 'success' : 'default'}>
          {record.is_active ? 'Faol' : 'Faol emas'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenDialog(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
      align: 'right',
      width: 100,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'stretch' : 'center',
          gap: 16,
          marginBottom: 24
        }}
      >
        <Title level={2} style={{ margin: 0 }}>Regionlarni boshqarish</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenDialog()}
          block={screens.xs}
        >
          Region qo&apos;shish
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <Table
        columns={columns}
        dataSource={regions}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        scroll={{ x: 800 }}
      />

      {/* Add/Edit Dialog */}
      <Modal
        title={editingRegion ? 'Regionni tahrirlash' : 'Yangi region qo\'shish'}
        open={openDialog}
        onCancel={handleCloseDialog}
        onOk={() => form.submit()}
        okText={editingRegion ? 'Yangilash' : 'Yaratish'}
        cancelText="Bekor qilish"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Region nomi"
            rules={[{ required: true, message: 'Iltimos region nomini kiriting' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Tavsif"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RegionManager;
